import { Radio as HeadlessRadio, RadioGroup } from '@headlessui/react';
import React from 'react';
import { Message, Radio, Toggle } from 'ui';
import ErrorMessage from '~/components/Form/ErrorMessage';
import HelpLink from '~/components/Links/HelpLink';
import ListFlowShippingDescription from '~/components/ListFlow/Descriptions/ListFlowShippingDescription';
import ListFlowSection from '~/components/ListFlow/ListFlowSection';
import ListFlowCustomParcelDetails from '~/components/ListFlow/ShippingSection/ListFlowCustomParcelDetails';
import ListFlowDiscountedShipping from '~/components/ListFlow/ShippingSection/ListFlowDiscountedShipping';
import ListFlowShippingCustomLabel from '~/components/ListFlow/ShippingSection/ListFlowShippingCustomLabel';
import ListFlowShippingInsurance from '~/components/ListFlow/ShippingSection/ListFlowShippingInsurance';
import { useListFlowCategory } from '~/hooks/listFlow/useListFlowCategories';
import { getListFlowCategory } from '~/store/listFlow/slices/info';
import {
  getListFlowCustomParcel,
  getListFlowDefaultParcel,
  getUsingOwnShippingLabel,
  isListFlowUsingDefaultParcel,
  isShippingDimensionsLocked,
  toggleUseDefaultParcel,
  usedOwnLabel,
} from '~/store/listFlow/slices/shipping';
import { getListFlowShippingError } from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

interface Props {}

const ListFlowShippingSection: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const error = useAppSelector(getListFlowShippingError);
  const lockedDimensions = useAppSelector(isShippingDimensionsLocked);
  const selectedCategory = useAppSelector(getListFlowCategory);
  const defaultParcel = useAppSelector(getListFlowDefaultParcel);
  const customParcel = useAppSelector(getListFlowCustomParcel);
  const useCategoryParcel = useAppSelector(isListFlowUsingDefaultParcel);
  const { data: category } = useListFlowCategory(selectedCategory?.id);
  const usingOwnLabel = useAppSelector(getUsingOwnShippingLabel);

  return (
    <ListFlowSection
      title="Shipping Details"
      description={<ListFlowShippingDescription />}
    >
      <RadioGroup
        className="rounded border"
        value={usingOwnLabel}
        onChange={value => dispatch(usedOwnLabel({ useOwnLabel: value }))}
      >
        <HeadlessRadio value={false}>
          {({ checked }) => (
            <div className="flex p-4">
              <div className="mr-8">
                <Radio checked={checked} className="mt-3 h-4 w-4" />
              </div>

              <div className="flex-1">
                <div className="font-semibold">
                  Use SidelineSwap Shipping Label
                </div>

                {useCategoryParcel && !!customParcel?.weight && (
                  <div className="text-slate-green-500 mt-1 text-sm">
                    Estimated Weight: {customParcel?.weight}lbs
                  </div>
                )}

                {checked && (
                  <>
                    {!lockedDimensions && (
                      <label className="mt-4 flex items-center space-x-2">
                        <Toggle
                          disabled={!defaultParcel}
                          onChange={enabled =>
                            dispatch(toggleUseDefaultParcel({ enabled }))
                          }
                          value={
                            useCategoryParcel == null ? true : useCategoryParcel
                          }
                        />
                        <span className="font-semibold">
                          Use suggested shipping dimensions
                        </span>
                      </label>
                    )}

                    {useCategoryParcel && category && (
                      <div className="mt-4">
                        <ListFlowDiscountedShipping category={category} />
                      </div>
                    )}

                    {!useCategoryParcel && <ListFlowCustomParcelDetails />}

                    <ListFlowShippingInsurance />
                  </>
                )}
              </div>
            </div>
          )}
        </HeadlessRadio>
        <hr />
        <HeadlessRadio value={true}>
          {({ checked }) => (
            <div className="flex p-4">
              <div className="mr-8">
                <Radio checked={checked} className="mt-3 h-4 w-4" />
              </div>

              <div className="flex-1">
                <div className="font-semibold">Use Your Own Shipping Label</div>

                <div className="text-slate-green-500 mt-1 text-sm">
                  For North American (US & CA) shipments only. Collect the
                  shipping cost, minus our standard seller fee, when the swap is
                  completed.{' '}
                  <HelpLink
                    link="customShippingCost"
                    className="text-blue-500 underline"
                  >
                    Learn more here.
                  </HelpLink>
                </div>

                {checked && <ListFlowShippingCustomLabel />}
              </div>
            </div>
          )}
        </HeadlessRadio>
      </RadioGroup>

      <ErrorMessage errors={error} />

      {category?.shipping_hint && (
        <Message info>{category.shipping_hint}</Message>
      )}
    </ListFlowSection>
  );
};

export default ListFlowShippingSection;
